import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { API_CONFIG } from './config';
import { GetAccessTokenUseCase, RefreshTokenUseCase } from '../../features/auth';
import { AuthStorage } from '../../features/auth/infrastructure/authStorage';
import { redirectToMainApp } from '../lib/mainAppUrl';

class HttpClient {
  private client: AxiosInstance;
  private getAccessTokenUseCase: GetAccessTokenUseCase;
  private refreshTokenUseCase: RefreshTokenUseCase;
  private authStorage: AuthStorage;

  constructor() {
    this.client = axios.create({
      baseURL: API_CONFIG.baseURL,
      timeout: API_CONFIG.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.getAccessTokenUseCase = new GetAccessTokenUseCase();
    this.refreshTokenUseCase = new RefreshTokenUseCase();
    this.authStorage = new AuthStorage();

    this.client.interceptors.request.use((config) => {
      const token = this.getAccessTokenUseCase.execute();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest?._retry) {
          originalRequest._retry = true;
          const newTokens = await this.refreshTokenUseCase.execute();
          if (newTokens) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newTokens.access}`;
            return this.client.request(originalRequest);
          }
          this.authStorage.clearAuthData();
          redirectToMainApp();
        }
        if (error.response?.status === 403) {
          const errorMessage = error.response?.data?.detail || error.response?.data?.error || '';
          console.error('403 Forbidden:', errorMessage);
          const shouldRedirectToMainApp =
            typeof errorMessage === 'string' &&
            (
              errorMessage.includes('двухфакторной авторизацией') ||
              errorMessage.includes('Сессия приложения') ||
              errorMessage.includes('is_staff')
            );
          if (shouldRedirectToMainApp) {
            this.authStorage.clearAuthData();
            redirectToMainApp();
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
}

export const httpClient = new HttpClient();
